import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, HouseIcon, MapPin } from "lucide-react";
import { Link, Navigate } from "react-router-dom";

import { routePaths } from "@/App";
import { NavigationSelect } from "@/components/NavigationSelect";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { useScanBarcode } from "@/hooks/useBarcodeScanner";
import { VehicleType } from "@/libs/api/types";
import { TourTaskItem } from "@/libs/tours-detail/components/TourTaskItem";
import { useTourNavigation } from "@/libs/tours-detail/hooks/useTourNavigation";
import { useTourStop } from "@/libs/tours-detail/hooks/useTourStop";
import { useTourStopLoader } from "@/libs/tours-detail/hooks/useTourStopLoader";
import { IconScan } from "@tabler/icons-react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

interface OrderPickUpViewProps {
  tourId: string;
  stopId: string;
}

export default function OrderPickUpView({
  tourId,
  stopId,
}: OrderPickUpViewProps) {
  const { t } = useTranslation();

  const { toast } = useToast();

  const { currentStop } = useTourStop(stopId);
  const { loadTourStop, loadAllTourStops } = useTourStopLoader();

  const { navigateToNextStop } = useTourNavigation(currentStop);

  const scanBarcode = useScanBarcode();

  const handleScanBarcode = useCallback(() => {
    scanBarcode()
      .then((result) => {
        const parentId = result.split("_").pop();

        if (!parentId || !currentStop) {
          return;
        }

        const task = currentStop.items.find(
          (item) => item.parentId === parentId,
        );
        if (!task) {
          return;
        }

        loadTourStop(parentId, task.id, true);
      })
      .catch(() => {
        toast({
          title: "Fehler beim Scannen des Barcodes",
          variant: "error",
        });
      });
  }, [loadTourStop, scanBarcode, toast, currentStop]);

  if (!currentStop) {
    return <Navigate to={routePaths.createTourPath(tourId)} />;
  }

  return (
    <div className="h-dvh flex flex-col">
      <div className="bg-white px-4 py-6 border-b-2 border-neutral pt-safe">
        <Link
          to={routePaths.createTourPath(tourId)}
          className="mb-8 flex items-center font-medium gap-1.5 bg-slate-100 w-fit p-2 pr-4 rounded"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
          {t("common.back")}
        </Link>

        <div className="flex flex-col gap-4">
          <h3 className="text-xl font-semibold flex items-center gap-2">
            {t("orderPickup.header.title", {
              count: currentStop.items.length,
            })}
            <Badge variant="secondary">{currentStop.items.length}</Badge>
          </h3>

          <div className="space-y-2">
            <div className="flex items-center text-sm font-medium text-[#6A80A8]">
              <HouseIcon className="w-5 h-5 mr-2" strokeWidth={2.5} />
              {currentStop.items[0].pharmacyName}
            </div>

            <div className="flex items-center text-sm font-medium text-[#6A80A8]">
              <MapPin className="w-5 h-5 mr-2" strokeWidth={2.5} />
              {currentStop.address}
            </div>

            <NavigationSelect
              address={currentStop.address}
              vehicle={VehicleType.CAR}
            />

            {!currentStop.items.every((item) => item.state === "done") && (
              <Button
                variant="outline"
                className="h-9 w-full mt-2 rounded-full hover:bg-slate-200"
                onClick={handleScanBarcode}
              >
                <IconScan className="mr-1 h-4 w-4" /> {t("common.scan")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 h-full space-y-4 overflow-y-scroll bg-list-background">
        {currentStop.items.map((item) => (
          <TourTaskItem
            key={item.id}
            item={item}
            onClick={() => loadTourStop(item.parentId, item.id)}
          />
        ))}
      </div>

      <div className="bg-white p-4 flex gap-4 border-t-2 border-neutral pb-safe">
        {!currentStop.items.every((item) => item.state === "done") && (
          <Button
            className="flex-1"
            variant="secondary"
            onClick={() => loadAllTourStops(currentStop)}
          >
            {t("orderPickup.collect.all")}
          </Button>
        )}
        <Button className="flex-1" onClick={navigateToNextStop}>
          {t("common.nextStop")}
        </Button>
      </div>
    </div>
  );
}

export function OrderPickUpSkeleton() {
  return (
    <div className="h-dvh flex flex-col">
      <div className="bg-white px-4 py-6 border-b-2 border-neutral pt-safe">
        <Skeleton className="mb-8 h-10 w-24 rounded" />

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-7 w-40 rounded" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center">
              <Skeleton className="h-5 w-48 rounded" />
            </div>

            <div className="flex items-center">
              <Skeleton className="h-5 w-64 rounded" />
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 h-full space-y-4 overflow-y-scroll bg-list-background">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="bg-white p-4 rounded-lg">
            <Skeleton className="h-6 w-3/4 mb-2 rounded" />
            <Skeleton className="h-4 w-1/2 mb-3 rounded" />
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-1/4 rounded" />
              <Skeleton className="h-8 w-20 rounded" />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white p-4 flex gap-4 border-t-2 border-neutral pb-safe">
        <Skeleton className="flex-1 h-12 rounded" />
        <Skeleton className="flex-1 h-12 rounded" />
      </div>
    </div>
  );
}
